import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DropdownMenuItem } from '@c2fo/liquidity';
import { EyeIcon, LockAltIcon } from '@c2fo/liquidity/icons';
import { ServiceAccount } from '../../api/ServiceAccounts';
import { ChangePasswordModal } from '../ChangePasswordModal';
import { useServiceAccountsState, type ServiceAccountsSortField } from '../../contexts/FileDeliveryContext';
import { TableHeader } from '../TableHeader';
import { DataTable, DataTableHead, DataTableBody, DataTableRow, DataTableCell } from '../DataTable';
import { ActionsMenu } from '../ActionsMenu';
import { useErrorState } from '../ErrorBanner';
import { formatDateWithTimezone } from '../../../../utils/dateFormat';

export const ServiceAccountsTable: React.FC = () => {
  const navigate = useNavigate();
  const { accounts, sortField, sortDirection, setSort, updatePassword } = useServiceAccountsState();
  const { addError } = useErrorState();
  const [passwordModalAccount, setPasswordModalAccount] = useState<ServiceAccount | null>(null);

  const handleSort = (field: ServiceAccountsSortField) => {
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSort(field, newDirection);
    } else {
      setSort(field, 'asc');
    }
  };

  return (
    <>
      <ChangePasswordModal
        isOpen={!!passwordModalAccount}
        accountName={passwordModalAccount?.name ?? ''}
        accountDisplayName={passwordModalAccount?.displayName ?? ''}
        onClose={() => setPasswordModalAccount(null)}
        onSave={updatePassword}
        onError={(message) => addError({ id: 'reset-password-error', message, type: 'error' })}
      />
      <DataTable>
        <DataTableHead>
          <TableHeader
            field="display_name"
            label="Name"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={handleSort}
            className="w-40"
          />
          <TableHeader label="Description" />
          <TableHeader
            field="create_time"
            label="Created"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={handleSort}
            className="w-44"
          />
          <TableHeader
            field="update_time"
            label="Updated"
            currentSortField={sortField}
            currentSortDirection={sortDirection}
            onSort={handleSort}
            className="w-44"
          />
          <TableHeader label="" className="w-16" />
        </DataTableHead>
        <DataTableBody>
          {accounts.map((account) => (
            <DataTableRow key={account.uid}>
              <DataTableCell variant="primary" className="whitespace-nowrap">
                {account.displayName}
              </DataTableCell>
              <DataTableCell variant="secondary">{account.description}</DataTableCell>
              <DataTableCell variant="date">
                {account.createTime ? formatDateWithTimezone(account.createTime) : '-'}
              </DataTableCell>
              <DataTableCell variant="date">
                {account.updateTime ? formatDateWithTimezone(account.updateTime) : '-'}
              </DataTableCell>
              <DataTableCell align="center">
                <ActionsMenu>
                  <DropdownMenuItem
                    className="active:bg-lightBlue-100"
                    onSelect={() => navigate(`/file-delivery/serviceAccounts/${account.uid}`)}
                  >
                    <EyeIcon className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="active:bg-lightBlue-100"
                    onSelect={() => setPasswordModalAccount(account)}
                  >
                    <LockAltIcon className="h-4 w-4 mr-2" />
                    Reset Password
                  </DropdownMenuItem>
                </ActionsMenu>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>
    </>
  );
};
